import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { LayoutDashboard, Calendar, TrendingUp, LogOut, Target, Award, CalendarDays, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TherapistDashboard = ({ user, onLogout }) => {
  const [incentiveData, setIncentiveData] = useState(null);
  const [propertyTherapists, setPropertyTherapists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch incentive data
      const incentiveResponse = await axios.get(`${API}/incentives/my-incentive`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncentiveData(incentiveResponse.data);
      
      // Fetch all therapists for this property (for shared login display)
      if (user.assigned_property_id) {
        const therapistsResponse = await axios.get(`${API}/therapists`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const propertyTherapists = therapistsResponse.data.filter(
          t => t.assigned_property_id === user.assigned_property_id
        );
        setPropertyTherapists(propertyTherapists);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get therapist names for welcome message
  const therapistNames = propertyTherapists.length > 0 
    ? propertyTherapists.map(t => t.full_name?.split(' ')[0] || t.full_name).join(', ')
    : user.full_name;

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-white/80 backdrop-blur-sm border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_wellness-erp-core/artifacts/fny25i7a_Logo.png" 
            alt="Staff Portal"
            className="h-12 w-auto"
          />
          <Button variant="outline" size="sm" onClick={onLogout} data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome Card with Property and All Therapist Names */}
        <div className="glass rounded-2xl p-6">
          {/* Property Name Badge */}
          {user.assigned_property_id && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium mb-3">
              <LayoutDashboard className="w-4 h-4" />
              {user.assigned_property_id}
            </div>
          )}
          
          {/* Welcome with all therapist names */}
          <h2 className="text-xl font-serif text-foreground mb-2">
            Welcome, {therapistNames}
          </h2>
          <p className="text-sm text-muted-foreground">Therapist Portal</p>
          
          {/* Show team badge if multiple therapists */}
          {propertyTherapists.length > 1 && (
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                <Users className="w-3 h-3 mr-1" />
                Team of {propertyTherapists.length} therapists
              </Badge>
            </div>
          )}
        </div>

        {/* Incentive/Target Progress Card */}
        {!loading && incentiveData && incentiveData.target > 0 && (
          <div className={`glass rounded-2xl p-6 ${incentiveData.actual_sales > incentiveData.threshold ? 'border-2 border-green-500 bg-green-50/30' : ''}`} data-testid="incentive-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Target className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-lg font-serif text-foreground">
                    Property Target Progress
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Collective target for all {incentiveData.therapist_count || 0} therapists
                  </p>
                </div>
              </div>
              {incentiveData.actual_sales > incentiveData.threshold && (
                <Badge className="bg-green-500 text-white animate-pulse">
                  <Award className="w-3 h-3 mr-1" />
                  Incentive Unlocked!
                </Badge>
              )}
            </div>

            <div className="space-y-6">
              {/* Progress Bar with Threshold Marker */}
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Progress</span>
                  <span className="text-sm font-medium text-foreground">{incentiveData.progress_percentage}%</span>
                </div>
                <div className="relative">
                  <Progress value={Math.min(incentiveData.progress_percentage, 100)} className="h-4" />
                  {/* Threshold marker */}
                  <div 
                    className="absolute top-0 h-4 w-0.5 bg-red-500"
                    style={{ left: `${incentiveData.threshold_percent || 90}%` }}
                    title={`${incentiveData.threshold_percent || 90}% Threshold`}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>₹0</span>
                  <span className="text-red-500 font-medium">{incentiveData.threshold_percent || 90}% threshold</span>
                  <span>₹{incentiveData.target?.toLocaleString()}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/30 rounded-xl p-4">
                  <p className="text-xs text-muted-foreground mb-1">Monthly Target</p>
                  <p className="text-xl font-medium text-foreground">₹{incentiveData.target?.toLocaleString()}</p>
                </div>
                <div className="bg-muted/30 rounded-xl p-4">
                  <p className="text-xs text-muted-foreground mb-1">Threshold ({incentiveData.threshold_percent || 90}%)</p>
                  <p className="text-xl font-medium text-foreground">₹{incentiveData.threshold?.toLocaleString()}</p>
                </div>
                <div className="bg-muted/30 rounded-xl p-4">
                  <p className="text-xs text-muted-foreground mb-1">Total Sales</p>
                  <p className={`text-xl font-medium ${incentiveData.actual_sales > incentiveData.threshold ? 'text-green-600' : 'text-accent'}`}>
                    ₹{incentiveData.actual_sales?.toLocaleString()}
                  </p>
                </div>
                <div className={`rounded-xl p-4 ${incentiveData.incentive_earned > 0 ? 'bg-green-100 border border-green-300' : 'bg-primary/10'}`}>
                  <p className="text-xs text-muted-foreground mb-1">
                    {incentiveData.incentive_earned > 0 ? 'Incentive Earned!' : 'Potential Incentive'}
                  </p>
                  <p className={`text-xl font-medium ${incentiveData.incentive_earned > 0 ? 'text-green-600' : 'text-primary'}`}>
                    ₹{incentiveData.incentive_earned?.toLocaleString()}
                  </p>
                  {incentiveData.incentive_earned > 0 && incentiveData.therapist_count > 1 && (
                    <p className="text-xs text-green-600 mt-1">
                      ₹{incentiveData.per_therapist_incentive?.toLocaleString()} per therapist
                    </p>
                  )}
                </div>
              </div>

              {/* Incentive Formula */}
              {incentiveData.incentive_earned > 0 && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-sm">
                  <p className="text-green-700">
                    <span className="font-medium">Calculation:</span> ₹{incentiveData.excess_amount?.toLocaleString()} above threshold × {incentiveData.incentive_percentage || 10}% = ₹{incentiveData.incentive_earned?.toLocaleString()}
                    {incentiveData.therapist_count > 1 && ` ÷ ${incentiveData.therapist_count} therapists = ₹${incentiveData.per_therapist_incentive?.toLocaleString()} each`}
                  </p>
                </div>
              )}

              {/* Amount needed to unlock incentive */}
              {incentiveData.actual_sales <= incentiveData.threshold && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-700">
                  <p>
                    <span className="font-medium">₹{(incentiveData.threshold - incentiveData.actual_sales).toLocaleString()}</span> more to unlock incentive
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* No target message */}
        {!loading && (!incentiveData || incentiveData.target === 0) && (
          <div className="glass rounded-2xl p-6 text-center">
            <Target className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">Monthly target not configured</p>
            <p className="text-sm text-muted-foreground">Contact admin to set up targets</p>
          </div>
        )}

        {/* Navigation Cards - Only 3: Attendance, Operations, Leave Management */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/therapist/attendance">
            <div className="glass rounded-2xl p-6 hover:shadow-float transition-all cursor-pointer" data-testid="attendance-nav">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-secondary-foreground" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground">Attendance</h3>
                  <p className="text-sm text-muted-foreground">
                    {propertyTherapists.length > 1 
                      ? 'PIN required • Select therapist to check-in' 
                      : 'Check-in & Check-out'}
                  </p>
                  <Badge variant="outline" className="mt-1 text-xs">
                    <Users className="w-3 h-3 mr-1" />
                    PIN Required
                  </Badge>
                </div>
              </div>
            </div>
          </Link>

          <Link to="/therapist/operations">
            <div className="glass rounded-2xl p-6 hover:shadow-float transition-all cursor-pointer border-2 border-primary/30" data-testid="operations-nav">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                  <CalendarDays className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground">Operations</h3>
                  <p className="text-sm text-muted-foreground">Room booking, Service Entry & History</p>
                  <Badge className="mt-1 text-xs bg-green-100 text-green-700 border-green-300">
                    Open Access • No PIN
                  </Badge>
                </div>
              </div>
            </div>
          </Link>

          <Link to="/therapist/leave">
            <div className="glass rounded-2xl p-6 hover:shadow-float transition-all cursor-pointer" data-testid="leave-nav">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-accent" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground">Leave Management</h3>
                  <p className="text-sm text-muted-foreground">
                    {propertyTherapists.length > 1 
                      ? 'Select therapist & enter PIN to apply' 
                      : 'Apply and track leaves'}
                  </p>
                  <Badge variant="outline" className="mt-1 text-xs">
                    <Users className="w-3 h-3 mr-1" />
                    PIN Required
                  </Badge>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TherapistDashboard;
